/**
 * MDI messaging utilities — unread message checking and staff message retrieval.
 *
 * Extracted from apps/platform so any partner app can use the same logic
 * against the MDI messaging API.
 */

import { getMessages, MDI_USER_TYPES, MDI_STATUS_NAMES } from "@repo/mdi";
import type { MDIPatientID, MDICaseID } from "@repo/db/branded-types";
import { asMDIPatientID, asMDICaseID } from "@repo/db/branded-types";
import type { Case, UnreadMessageInfo, StaffMessage } from "..";

// ── Types ────────────────────────────────────────────────────────────────

/** Raw message shape returned by the MDI messaging API */
type Message = {
  user_type: string;
  created_at: string;
  text: string;
  read_message_info?: string | null | undefined;
};

// ── Helpers ──────────────────────────────────────────────────────────────

/**
 * Returns true when `userType` represents any MDI staff role
 * (clinician, support, or their namespaced variants).
 */
export function isStaffMessage(userType: string): boolean {
  return (
    userType === MDI_USER_TYPES.CLINICIAN ||
    userType === MDI_USER_TYPES.CLINICIAN_NAMESPACED ||
    userType === MDI_USER_TYPES.SUPPORT ||
    userType === MDI_USER_TYPES.SUPPORT_NAMESPACED ||
    userType === MDI_USER_TYPES.SUPPORT_STAFF
  );
}

/**
 * Filters messages to find the most recent message from staff members
 * (clinicians or support staff).
 *
 * @param messages - Array of messages from the MDI chat
 * @returns The most recent staff message or null if no staff messages exist
 */
export function getMostRecentStaffMessage(
  messages: Message[]
): StaffMessage | null {
  const staffMessages = messages.filter((msg) => isStaffMessage(msg.user_type));

  if (staffMessages.length === 0) return null;

  const mostRecent = staffMessages.reduce((latest, current) =>
    new Date(current.created_at) > new Date(latest.created_at)
      ? current
      : latest
  );

  return {
    text: mostRecent.text,
    read_message_info: mostRecent.read_message_info,
    created_at: mostRecent.created_at,
  };
}

// ── Unread message checking ──────────────────────────────────────────────

/**
 * Check if a patient has unread messages in their most recent conversation.
 * Assumes a patient typically has one active case at a time.
 *
 * @param patientId - The MDI patient ID
 * @returns Object with hasUnread flag and message type (clinician or support)
 */
export async function checkPatientHasUnreadMessages(
  patientId: MDIPatientID
): Promise<UnreadMessageInfo> {
  try {
    const response = await getMessages({
      path: { patient: patientId },
      query: {
        channel: "patient",
        per_page: "10",
        page: "1",
      },
    });

    if (!response.data || !response.data.data) {
      return { hasUnread: false };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const messages = response.data.data as any[];

    if (messages.length === 0) {
      return { hasUnread: false };
    }

    for (const message of messages) {
      const isClinicianMessage =
        message.user_type === MDI_USER_TYPES.CLINICIAN ||
        message.user_type === MDI_USER_TYPES.CLINICIAN_NAMESPACED;
      const isSupportMessage =
        message.user_type === MDI_USER_TYPES.SUPPORT ||
        message.user_type === MDI_USER_TYPES.SUPPORT_NAMESPACED ||
        message.user_type === MDI_USER_TYPES.SUPPORT_STAFF ||
        message.user_type === "SupportStaff"; // Legacy format

      if (
        (isClinicianMessage || isSupportMessage) &&
        message.read_message_info !== "Read"
      ) {
        const messageType = isSupportMessage ? "support" : "clinician";
        return { hasUnread: true, messageType };
      }
    }

    return { hasUnread: false };
  } catch (error) {
    console.error(
      "Error checking unread messages for patient:",
      patientId,
      error
    );
    return { hasUnread: false };
  }
}

/**
 * Batch check for unread messages for multiple visits.
 * Returns a map of case_id to UnreadMessageInfo.
 */
export async function batchCheckUnreadMessages(
  visits: Array<{ case_id: string; patient?: { patient_id: string } }>
): Promise<Map<string, UnreadMessageInfo>> {
  const unreadMap = new Map<string, UnreadMessageInfo>();

  await Promise.all(
    visits.map(async (visit) => {
      if (visit.patient?.patient_id) {
        const unreadInfo = await checkPatientHasUnreadMessages(
          asMDIPatientID(visit.patient.patient_id)
        );
        unreadMap.set(visit.case_id, unreadInfo);
      } else {
        unreadMap.set(visit.case_id, { hasUnread: false });
      }
    })
  );

  return unreadMap;
}

// ── Staff message retrieval ──────────────────────────────────────────────

/**
 * Fetches the most recent staff message for a patient from the MDI messaging API.
 *
 * @param patientId - The MDI provider network member ID (providerNetworkMemberId)
 * @returns The most recent staff message, or null if none found
 */
export async function getLastStaffMessage(
  patientId: string
): Promise<StaffMessage | null> {
  const { data } = await getMessages({
    path: { patient: patientId },
    query: { channel: "patient" },
  });

  if (!data || !data.data || data.data.length === 0) {
    return null;
  }

  const parsed: Message[] = data.data.map((msg) => ({
    user_type: msg.user_type!,
    created_at: msg.created_at!,
    text: msg.text!,
    read_message_info: msg.read_message_info ?? null,
  }));

  return getMostRecentStaffMessage(parsed);
}

// ── Visit unread info ────────────────────────────────────────────────────

/**
 * Checks if a visit should show unread message notifications.
 * Only returns unread info for visits in active MDI statuses
 * (assigned, approved, support, waiting, processing).
 *
 * @param visit - The case to check
 * @param unreadMap - Map of case IDs to their unread message info
 * @returns The unread info if there are unread messages, undefined otherwise
 */
export function getUnreadMessageInfo(
  visit: Case,
  unreadMap?: Map<MDICaseID, UnreadMessageInfo>
): UnreadMessageInfo | undefined {
  const mdiStatus = visit.case_status?.name?.toLowerCase();

  if (
    (mdiStatus === MDI_STATUS_NAMES.ASSIGNED ||
      mdiStatus === MDI_STATUS_NAMES.APPROVED ||
      mdiStatus === MDI_STATUS_NAMES.SUPPORT ||
      mdiStatus === MDI_STATUS_NAMES.WAITING ||
      mdiStatus === MDI_STATUS_NAMES.PROCESSING) &&
    unreadMap &&
    visit.case_id
  ) {
    const info = unreadMap.get(asMDICaseID(visit.case_id));
    if (info?.hasUnread) {
      return info;
    }
  }

  return undefined;
}
